import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import { http, createConfig, createStorage, webSocket } from 'wagmi'
import { useAccount, useConnect, useDisconnect, useWalletClient } from "wagmi";
import { arbitrum, base, mainnet, optimism, sepolia } from 'wagmi/chains'
import { injected, safe, walletConnect } from 'wagmi/connectors'
import { useEnsAvatar, useEnsName } from 'wagmi'
import { Line } from "react-chartjs-2";
import contractABI from "./contracts/AppleTree.json";
import KYCCompleted from "./KYCCompleted";
import positionManagerABI from "./contracts/NonfungiblePositionManagerABI.json";
import poolABI from "./contracts/UniswapPoolABI.json";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import logo from "./LogoAppleTreeHD.svg";
import BuyAPTESG from "./contracts/BuyAPTESG";
import { Link } from "react-router-dom";
import LegalFooter from "./components/legal/LegalFooter";
import { useLiquidityStatus } from "./components/liquidity/useLiquidityStatus";
import { useWalletLiquidity } from "./components/liquidity/useWalletLiquidity";
import {
  APTESG_ADDRESS,
  APTESG_POOL_ADDRESS,
  ARBITRUM_CHAIN_ID,
  ARBITRUM_RPC_URL,
  ensureArbitrumNetwork,
  getArbitrumReadProvider,
  getFriendlyWalletError,
  getWalletProvider,
} from "./config/arbitrum";

// Legacy participation cards remain in the codebase so they can be restored later.
const SHOW_LEGACY_HOME_CARDS = false;

const getProvider = (walletClient, options = {}) => {
  const { requireSigner = false } = options;
  if (!requireSigner) return getArbitrumReadProvider();
  return getWalletProvider(walletClient);
};



export const wagmiConfig = createConfig({
  connectors: [
    // 🔴 EIP-6963: Auto-discovery protocol (rileva tutti i wallet installati)
    injected({
      shimDisconnect: true, // Compatibilità extra per alcuni wallet
    }),
    // 🟣 WalletConnect: Supporto universale per wallet mobili e browser senza estensione
    walletConnect({ 
      projectId: "f123d6b14f3a6461033ba05fd54cad5e", 
      showQrModal: true,
      qrModalOptions: {
        themeMode: 'dark', // Migliore visibilità su dark mode
      }
    }),
    // 🟠 Safe (ex Gnosis): Per account multisig
    safe(),
  ],
  chains: [arbitrum, mainnet, base, optimism, sepolia],
  transports: {
    [arbitrum.id]: http(ARBITRUM_RPC_URL),
    [mainnet.id]: http(),
    [base.id]: http(),
    [optimism.id]: http(),
    [sepolia.id]: http(),
  },
  storage: createStorage({
    storage: window.localStorage,
  }),
});


// Indirizzi dei contratti
// Usa l'indirizzo dal file .env per coerenza tra le pagine
const contractAddress = (
  (typeof import.meta !== "undefined" && import.meta.env && (import.meta.env.REACT_APP_APPLETREE_CONTRACT || import.meta.env.VITE_APPLETREE_CONTRACT))
  || (typeof process !== "undefined" && process.env && process.env.REACT_APP_APPLETREE_CONTRACT)
  || "0x58665Cf4742476532CA2387Bcc1631Af21C10c79"
); // Contratto AppleTree principale (Arbitrum)
const positionManagerAddress = "0xC36442b4a4522E871399CD717aBDD847Ab11FE88"; // Arbitrum Mainnet
const poolAddress = APTESG_POOL_ADDRESS;
const aptESGTokenAddress = APTESG_ADDRESS;

// ABI ERC20 minimale per leggere balance
const erc20ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)"
];


const switchToArbitrum = async (walletClient, connector) => {
  try {
    await ensureArbitrumNetwork(walletClient, connector);
    return true;
  } catch (error) {
    console.error("Unable to switch the active wallet to Arbitrum One", error);
    alert(getFriendlyWalletError(error));
    return false;
  }
};


const checkTreasuryBalance = async (walletClient, kycFee) => {
  const provider = getProvider(walletClient);
  const contract = new ethers.Contract(contractAddress, contractABI, provider);
  const treasury = await contract.treasury();
  const balance = await provider.getBalance(treasury);

  console.log("📌 Treasury balance:", ethers.utils.formatEther(balance));

  if (parseFloat(ethers.utils.formatEther(balance)) < parseFloat(kycFee)) {
    alert("⚠️ Treasury does not have enough ETH to complete the KYC transaction.");
    return false;
  }
  return true;
};



const checkTreasuryConfig = async (walletClient) => {
  const provider = getProvider(walletClient);
  const contract = new ethers.Contract(contractAddress, contractABI, provider);

  const treasury = await contract.treasury();
  console.log("📌 Treasury attuale:", treasury);

  if (treasury === "0x0000000000000000000000000000000000000000") {
    alert("⚠️ The Treasury is not set! An administrator must set this up first.");
    return false;
  }
  return true;
};

const checkKYCProvider = async (walletClient) => {
  const provider = getProvider(walletClient);
  const contract = new ethers.Contract(contractAddress, contractABI, provider);
  const kycProvider = await contract.kycProvider();

  if (kycProvider === "0x0000000000000000000000000000000000000000") {
    alert("⚠️ KYC Provider is not set! Please contact the admin.");
    return false;
  }
  return true;
};

const donateToTreasury = async (walletClient, amount) => {
  try {
    amount = amount?.toString().trim().replace("," , ".");
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      alert("Please enter a valid amount.");
      return;
    }

    const provider = getProvider(walletClient, { requireSigner: true });
    if (!provider) {
      alert("❌ Wallet not connected. Please reconnect.");
      return;
    }
    const signer = provider.getSigner();

    const contract = new ethers.Contract(contractAddress, contractABI, provider);
    let treasuryAddress = await contract.treasury();

    treasuryAddress = ethers.utils.getAddress(treasuryAddress);

    const tx = await signer.sendTransaction({
      to: treasuryAddress,
      value: ethers.utils.parseEther(amount.toString()),
    });

    console.log("✅ Donation sent:", tx.hash);
    alert("Thank you for your donation! TX: " + tx.hash);
  } catch (error) {
    console.error("❌ Error sending donation:", error);
    alert("Error: " + error.message);
  }
};

const checkIfAlreadyPaid = async (walletClient, userAddress) => {
  if (!userAddress) return false;
  const provider = getProvider(walletClient);
  const contract = new ethers.Contract(contractAddress, contractABI, provider);

  const isPaid = await contract.kycPaid(userAddress);
  console.log("📌 Have you already paid the KYC?", isPaid);

  if (isPaid) {
    alert("⚠️ You have already paid the KYC! You can't pay it twice.");
    return false;
  }
  return true;
};


// ✅ Riavvia la sessione KYC se l'utente ha già pagato ma non completato la verifica
const resumeKYC = async (walletClient, userAddress) => {
  if (!userAddress) return;
  const provider = getProvider(walletClient);

  try {
    const response = await fetch("https://aptesg-backend-aae2ea58fed0.herokuapp.com/start-kyc-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ wallet: userAddress }),
    });

    const data = await response.json();
    if (data.success && data.verification_url) {
      window.open(data.verification_url, "_blank");
    } else {
      alert("❌ Failed to resume KYC session. Please contact support.");
    }
  } catch (error) {
    console.error("❌ Error resuming KYC session:", error);
    alert("Unexpected error while resuming KYC session.");
  }
};


// Componente DexTools Widget per APTESG
const DexToolsWidget = () => {
  return (
    <div style={{ width: "100%", height: "450px", borderRadius: "12px", overflow: "hidden" }}>
      <iframe 
        src={`https://www.dextools.io/widget-chart/en/arbitrum/pe-light/${APTESG_POOL_ADDRESS}?theme=dark&chartType=2&chartResolution=30&drawingToolbars=false`}
        style={{ 
          width: "100%", 
          height: "100%", 
          border: "none",
          borderRadius: "12px"
        }}
        title="DexTools APTESG Chart"
      />
    </div>
  );
};


function App() {
  const [kycFee, setKycFee] = useState(0);
  const [kycStatus, setKycStatus] = useState(false);
  const [tokenPrice, setTokenPrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [amount, setAmount] = useState("");
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);
  const [treasuryBalance, setTreasuryBalance] = useState(0);
  const [treasuryTokenBalance, setTreasuryTokenBalance] = useState(0);
  const [ethPriceUSD, setEthPriceUSD] = useState(null);
  const [aptESGPriceUSD, setAptESGPriceUSD] = useState(null);
  const [batchSize, setBatchSize] = useState("");
  const { address, isConnected, chain, connector } = useAccount(); // Informazioni sull'account con chain
  const { connectAsync, connectors } = useConnect(); // Connettori disponibili
  const { disconnect } = useDisconnect(); // Funzione per disconnettere il wallet
  const {data: walletClient } = useWalletClient();
  const { data: ensName } = useEnsName({ address }); // Ottieni il nome ENS
  const { data: ensAvatar } = useEnsAvatar({ name: ensName }); // Ottieni l'avatar ENS
  const [tokenRatio, setTokenRatio] = useState(null);
  const [donationAmount, setDonationAmount] = useState("");
  const [backendAddress, setBackendAddress] = useState("");
  const [ethAmount, setEthAmount] = useState("");
  const [timeRemaining, setTimeRemaining] = useState("No deposit found");
  const [bonusAmount, setBonusAmount] = useState("Loading...");
  const [stakingAmount, setStakingAmount] = useState("");
  const [stakingDetails, setStakingDetails] = useState(null);
  const [timeRemainingStaking, setTimeRemainingStaking] = useState("");
  const [claimedBonus, setClaimedBonus] = useState("");
  const [estimatedLiquidityBonus, setEstimatedLiquidityBonus] = useState("Enter an amount");
  const [estimatedStakingBonus, setEstimatedStakingBonus] = useState("Enter an amount");
  const [hasPaidButNotVerified, setHasPaidButNotVerified] = useState(false);
  const [wrongNetwork, setWrongNetwork] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshTriggeredRef = useRef(false);
  const certifyRef = useRef(null);
  const [vestedTokens, setVestedTokens] = useState(0);

useEffect(() => {
  if (isConnected && address) {
    fetchVestedTokens();
    // const interval = setInterval(fetchVestedTokens, 60000); // aggiorna ogni 60 sec - TEMPORARILY DISABLED
    // return () => clearInterval(interval);
  }
}, [isConnected, address]);

  useEffect(() => {
    console.log("App.jsx file loaded successfully");
  }, []);

  const scrollToCertify = () => {
    setTimeout(() => {
      certifyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50); // workaround se il DOM non è ancora pronto
  };


  useEffect(() =>{
    console.log("refreshkey updated to:", refreshKey);
  }, [refreshKey]);

  // Funzione per verificare lo stato KYC dall'API del backend
  const checkKYCStatus = async (walletClient) => {
    if (!isConnected || !address) return;
 
    try {
      const provider = getProvider();
      if (!provider) {
        console.warn("Provider not available");
        return;
      }
 
      const contractInstance = new ethers.Contract(contractAddress, contractABI, provider);
 
      const isVerifiedOnChain = await contractInstance.verifiedInvestors(address);
      setKycStatus(isVerifiedOnChain);
      console.log("✅ On-chain KYC status:", isVerifiedOnChain);
 
      const paid = await contractInstance.kycPaid(address);
      console.log("📌 kycPaid:", paid);
 
      setHasPaidButNotVerified(paid && !isVerifiedOnChain);
    } catch (error) {
      console.error("❌ Error retrieving KYC status:", error.message);
      setKycStatus(false);
      setHasPaidButNotVerified(false);
    }
  };

  // Esegui il controllo dello stato KYC quando il wallet è connesso
  useEffect(() => {
    if (isConnected) {
      checkKYCStatus(walletClient);
    }
  }, [isConnected, address]);

  useEffect(() => {
    if (!isConnected || !address) return;
    // const interval = setInterval(() => {
    //   checkKYCStatus(walletClient);
    // }, 10000); // ogni 10 secondi - TEMPORARILY DISABLED
    // return () =>
    //   clearInterval(interval); //pulizia al cambio wallet
  }, [isConnected, address]);


  useEffect(() => {
    if (isConnected) {
      fetchTreasuryBalance();
      fetchTreasuryTokenBalance();
      fetchEthPrice();
      fetchAPTESGPrice();
      // const interval = setInterval(fetchTreasuryBalance, 80000); // ogni 80s - TEMPORARILY DISABLED
      // return () => clearInterval(interval);
    }
  }, [isConnected, address]);


  useEffect(() => {
    if (!address) return;
 
    const ws = new WebSocket("wss://aptesg-backend-aae2ea58fed0.herokuapp.com");
 
    ws.onopen = () => {
      console.log("✅ Connected to KYC WebSocket server");
    };

    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ping"}));
      }
    }, 3000);
 
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "kycUpdate" && data.wallet.toLowerCase() === address.toLowerCase()) {
          console.log("✅ KYC updated via WebSocket!");
          setKycStatus(true);
          alert("✅ KYC verification completed! You can now provide liquidity or stake your tokens.");
        }
      } catch (err) {
        console.error("Error parsing WebSocket message:", err);
      }
    };
 
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
 
    ws.onclose = () => {
      console.log("🔌 WebSocket connection closed");
    };
 
    return () => {
      clearInterval(pingInterval);
    ws.close();
    };
  }, [address]);


  // WalletKit non necessario: gestiamo le connessioni via wagmi connectors

  // Rimosso: inizializzazione manuale di WalletKit per evitare doppia init con wagmi


  // Funzione per connettersi al wallet
  const connectWallet = async () => {
    console.log("🔄 Attempting to connect to wallet...");
 
    try {
      if (isConnected) {
        console.log("🔗 Wallet already connected:", address);
        await switchToArbitrum(walletClient, connector);
        return;
      }
 
      const targetChainId = arbitrum.id;
      const injectedConnector = connectors.find((c) => c.id === "injected");
      const walletConnectConnector = connectors.find((c) => c.id === "walletConnect");

      const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const hasEthereumProvider = typeof window.ethereum !== 'undefined';
 
      if (isMobile) {
        // 📱 Mobile: Prova prima l'estensione del browser se presente
        if (hasEthereumProvider && injectedConnector) {
          console.log("📲 Wallet extension rilevato in mobile browser");
          await connectAsync({ connector: injectedConnector, chainId: targetChainId });
          return;
        }

        // 📱 Mobile fallback: WalletConnect QR
        if (walletConnectConnector) {
          console.log("📲 Mobile flow - usando WalletConnect QR per connessione universale");
          await connectAsync({ connector: walletConnectConnector, chainId: targetChainId });
          return;
        }

        alert("❌ Nessun connettore disponibile. Installa un wallet Web3 (MetaMask, Trust Wallet, Rainbow, ecc).");
        return;
      }
 
      // 💻 Desktop: Prova prima l'estensione del browser
      if (hasEthereumProvider && injectedConnector) {
        console.log("✅ Provider Ethereum rilevato (MetaMask, Brave, Coinbase, ecc)");
        await connectAsync({ connector: injectedConnector, chainId: targetChainId });
        return;
      }
      
      // 💻 Desktop fallback: WalletConnect per wallet mobili o browser desktop senza estensione
      if (walletConnectConnector) {
        console.log("⚠️ Nessun provider nel browser, usando WalletConnect come fallback");
        await connectAsync({ connector: walletConnectConnector, chainId: targetChainId });
        return;
      }
      
      alert("❌ Nessun wallet disponibile. Opzioni:\n1. Installa MetaMask/Brave/Coinbase extension\n2. Connetti tramite WalletConnect\n3. Usa Trust Wallet su mobile");
      return;
 
    } catch (error) {
      console.error("❌ Errore durante la connessione:", error);
      alert("❌ Errore connessione wallet: " + error.message);
    }
  };


  // Recupera la tariffa KYC
  useEffect(() => {
    if (isConnected && address) {
      const fetchKycFee = async () => {
        try {
          const provider = getProvider();
          const contract = new ethers.Contract(contractAddress, contractABI, provider);

          const fee = await contract.kycFee();
          console.log("Retrieved KYC Fee:", ethers.utils.formatEther(fee));
          setKycFee(ethers.utils.formatEther(fee));
        } catch (error) {
          console.error("Error fetching KYC fee:", error);
        }
        if (isConnected) {
          checkKYCStatus(walletClient);
          checkKYCProvider(walletClient);
        }
      };

      fetchKycFee();
    }
  }, [isConnected, address]);

  // Funzione per pagare la tariffa KYC
  const payForKYC = async (userAddress) => {
    try {
      if (!isConnected) {
        alert("⚠️ Connect your wallet first!");
        return;
      }

      if (!userAddress) {
        alert("❌ Wallet address not found! Try reconnecting your wallet.");
        return;
      }
      console.log("📌 Connected wallet:", userAddress);

      if (!(await switchToArbitrum(walletClient, connector))) return;
      if (!(await checkTreasuryConfig(walletClient))) return;
      if (!(await checkTreasuryBalance(walletClient, kycFee))) return;
      if (!(await checkIfAlreadyPaid(walletClient, userAddress))) return;

      const provider = getProvider(walletClient, { requireSigner: true });
      if (!provider) {
        alert("❌ Wallet not connected. Please reconnect.");
        return;
      }
      await provider.send("eth_requestAccounts", []);
      const signer = provider.getSigner();
     
      // ✅ Recupera il Treasury dal contratto
      const contract = new ethers.Contract(contractAddress, contractABI, signer);
      let treasuryAddress = await contract.treasury();
      let kycProviderAddress = await contract.kycProvider();
   
      // ✅ Converte in checksum address
      try {
        treasuryAddress = ethers.utils.getAddress(treasuryAddress);
        kycProviderAddress = ethers.utils.getAddress(kycProviderAddress);
        console.log("📌 Treasury Checksum Address:", treasuryAddress);
        console.log("📌 KYC Provider Checksum Address:", kycProviderAddress);
      } catch (error) {
        console.error("❌ Address checksum error:", error);
        alert("Error: Invalid addresses.");
        return;
      }

      // ✅ Controlla saldo ETH dell'utente per il gas
      const balance = await provider.getBalance(userAddress);
      const ethBalance = ethers.utils.formatEther(balance);
      if (parseFloat(ethBalance) < parseFloat(kycFee)) {
        alert("⚠️ Insufficient ETH balance for gas fees!");
        return;
      }

      const feeToPay = ethers.utils.parseEther(kycFee.toString());

      console.log("📌 KYC payment attempt...");
      console.log("💰 KYC Amount Required:", kycFee, "ETH");
      console.log("💰 Amount that will be sent:", ethers.utils.formatEther(feeToPay), "ETH");
      console.log("🏦 Treasury Address:", treasuryAddress);
      console.log("👤 User Address:", userAddress);
      console.log("🛂 KYC Provider Address:", kycProviderAddress);
      console.log("🛠️ Call attempt payForKYC()...");

        const tx = await contract.payForKYC({
          value: feeToPay,
          gasLimit: ethers.utils.hexlify(1000000), // Aumentato il gas limit
        });

        console.log("✅ Transaction sent:", tx.hash);
        await tx.wait();
        alert("🎉 KYC Payment successful! Starting identity verification...");

      // Avvia sessione KYC con il backend
      try {
        const response = await fetch("https://aptesg-backend-aae2ea58fed0.herokuapp.com/start-kyc-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ wallet: userAddress }),
        });
       
        const data = await response.json();
        if (data.success && data.verification_url) {
          window.open(data.verification_url, "_blank");
        } else {
          alert("❌ Failed to start KYC session. Please contact support.");
        }
      } catch (error) {
        console.error("❌ Error starting KYC session:", error);
        alert("Unexpected error during KYC session");
      }
 
    } catch (txError) {
      console.error("❌ Error during payForKYC():", txError);
      alert("❌ KYC Error: " + (txError.reason || txError.message));
    }
  };
 


  // Monitoraggio del prezzo del token
  const fetchTokenPriceFromPool = async () => {
    try {
      const provider = getProvider(walletClient);
      if (!provider) return null; // Silenzioso se provider non disponibile
      const poolContract = new ethers.Contract(poolAddress, poolABI, provider);
      const slot0 = await poolContract.slot0();
      const sqrtPriceX96 = slot0[0]; // Il primo valore è sqrtPriceX96

      const tokenPriceInETH = (Number(sqrtPriceX96) / (2 ** 96)) ** 2;
      setTokenRatio(tokenPriceInETH);
      console.log("Token price (APTESG in ETH):", tokenPriceInETH);
      setTokenPrice(tokenPriceInETH);
      
      // Aggiungi al priceHistory per il grafico
      setPriceHistory(prev => {
        const newHistory = [
          ...prev,
          {
            time: new Date().toLocaleTimeString(),
            price: tokenPriceInETH
          }
        ];
        // Mantieni solo gli ultimi 50 punti
        return newHistory.slice(-50);
      });
    } catch (error) {
      console.error("Error retrieving price from pool:", error);
      return null;
    }
  };


  // Esegui il recupero del prezzo automaticamente ogni 30 secondi
  useEffect(() => {
    // Esegui subito al caricamento
    fetchTokenPriceFromPool();
    
    // Poi ogni 30 secondi
    const interval = setInterval(() =>
      fetchTokenPriceFromPool(), 30000);
    
    return () => clearInterval(interval);
  }, []); // Rimosso isConnected per farlo funzionare sempre



  //funzione per recuperare il rapporto dal pool
  const fetchTokenRatio = async () => {
    try {
      const provider = getProvider(walletClient);
      if (!provider) return; // Silenzioso se provider non disponibile
      const poolContract = new ethers.Contract(poolAddress, poolABI, provider);

      // Uniswap V3 usa slot0() per ottenere il prezzo
      const slot0 = await poolContract.slot0();
      const sqrtPriceX96 = slot0[0]; // sqrtPriceX96 è il primo valore restituito

      // Calcola il rapporto di prezzo
      const tokenPriceInETH = (Number(sqrtPriceX96) / (2 ** 96)) ** 2;

      setTokenRatio(tokenPriceInETH);
      console.log("✅ Token ratio fetched:", tokenPriceInETH);
    } catch (error) {
      console.error("❌ Error fetching token ratio:", error);
    }
  };
  // Recupera il rapporto quando la dApp è caricata
  useEffect(() => {
    if (walletClient) fetchTokenRatio(walletClient); },
    [walletClient]);

  // recupera saldo treasury
  const fetchTreasuryBalance = async () => {
    if (!isConnected || !address) return;
    try {
      const provider = getProvider(walletClient);
      if (!provider) {
        console.warn("Provider not available");
        return;
      }
      const contract = new ethers.Contract(contractAddress, contractABI, provider);
      const treasuryAddress = await contract.treasury();
      const balance = await provider.getBalance(treasuryAddress);
      const ethFormatted = ethers.utils.formatEther(balance);
      setTreasuryBalance(ethFormatted);
      console.log("✅ Treasury balance:", ethFormatted, "ETH");
    } catch (error) {
      console.error("❌ Error fetching treasury balance:", error);
      setTreasuryBalance("0");
    }
  };

  const fetchTreasuryTokenBalance = async () => {
    if (!isConnected || !address) return;
    try {
      const provider = getProvider(walletClient);
      if (!provider) {
        console.warn("Provider not available");
        return;
      }
      const contract = new ethers.Contract(contractAddress, contractABI, provider);
      const treasuryAddress = await contract.treasury();
      
      // Leggi il balance del token APTESG nella treasury
      const tokenContract = new ethers.Contract(aptESGTokenAddress, erc20ABI, provider);
      const balance = await tokenContract.balanceOf(treasuryAddress);
      const decimals = await tokenContract.decimals();
      const tokenFormatted = ethers.utils.formatUnits(balance, decimals);
      setTreasuryTokenBalance(tokenFormatted);
      console.log("✅ Treasury APTESG balance:", tokenFormatted, "APTESG");
    } catch (error) {
      console.error("❌ Error fetching treasury token balance:", error);
      setTreasuryTokenBalance("0");
    }
  };

  const fetchEthPrice = async () => {
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
      const data = await response.json();
      const price = data.ethereum.usd;
      setEthPriceUSD(price);
      console.log("✅ ETH price:", price, "USD");
    } catch (error) {
      console.error("❌ Error fetching ETH price:", error);
    }
  };

  const fetchAPTESGPrice = async () => {
    try {
      // Usa GeckoTerminal per ottenere il prezzo del token APTESG su Arbitrum
      const response = await fetch(`https://api.geckoterminal.com/api/v2/networks/arbitrum/tokens/${aptESGTokenAddress.toLowerCase()}`);
      const data = await response.json();
      
      if (data.data && data.data.attributes && data.data.attributes.price_usd) {
        const price = parseFloat(data.data.attributes.price_usd);
        setAptESGPriceUSD(price);
        console.log("✅ APTESG price (GeckoTerminal):", price, "USD");
      } else {
        console.warn("⚠️ APTESG price not found on GeckoTerminal");
      }
    } catch (error) {
      console.error("❌ Error fetching APTESG price:", error);
    }
  };

  // funzione provideLiquidity

  async function checkAndProvideLiquidity(walletClient, amountInETH) {
  if (!kycStatus) {
    alert("⚠️ You must complete KYC before providing liquidity!");
    return;
  }
  const provider = getProvider(walletClient, { requireSigner: true });
  if (!provider) {
    alert("❌ Wallet not connected. Please reconnect.");
    return;
  }

  await provider.send("eth_requestAccounts", []);
  const signer = provider.getSigner();
  const contract = new ethers.Contract(contractAddress, contractABI, signer);

  // Leggi l'ultimo timestamp di deposito
  const lastDeposit = await contract.depositTimestamp(address);
  const now = Math.floor(Date.now() / 1000);
  const secondsSinceLastDeposit = now - lastDeposit.toNumber();

  if (lastDeposit.toNumber() !== 0 && secondsSinceLastDeposit < 30 * 24 * 60 * 60) {
    const daysLeft = Math.ceil((30 * 24 * 60 * 60 - secondsSinceLastDeposit) / (24 * 60 * 60));
    alert(`⚠️ You can provide liquidity only once every 30 days. Please wait ${daysLeft} more day(s).`);
    return;
  }

  // Se tutto ok, procedi con la chiamata
  await provideLiquidity(walletClient, amountInETH);
}


  async function provideLiquidity(walletClient, amountInETH) {
    try {
      if (!amountInETH || isNaN(amountInETH) || parseFloat(amountInETH) <= 0) {
        alert("❌ Invalid ETH amount");
        return;
      }
     
      console.log("Providing liquidity with amount:", amountInETH);

      if (!isConnected || !address) {
        alert("⚠️ Connect your wallet first!");
        return;
      }

      // Assicurati che l'utente sia sulla rete Arbitrum
      if (!(await switchToArbitrum(walletClient, connector))) return;

      // Controlla che l'utente sia KYC verificato
      const provider = getProvider(walletClient, { requireSigner: true });
      if (!provider) {
        alert("❌ Wallet not connected. Please reconnect.");
        return;
      }
      const signer = provider.getSigner();
      const contract = new ethers.Contract(contractAddress, contractABI, signer);
      const verified = await contract.verifiedInvestors(address);

      // Converti ETH in Wei
      const amountInWei = ethers.utils.parseEther(amountInETH.toString());

      // Controlla il saldo dell'utente
      const balance = await provider.getBalance(address);
      if (balance.lt(amountInWei)) {
        alert("❌ Insufficient ETH balance!");
        return;
      }

      console.log("📌 ETH to send:", amountInETH, "ETH");

      // controlliamo se è verificato kyc
      if (!verified) {
        alert("⚠️ You must complete KYC before providing liquidity!");
        return;
      }

      // Chiamata alla funzione `provideLiquidity()` dello smart contract
      const tx = await contract.provideLiquidity({
        value: amountInWei, // L'utente invia ETH
        gasLimit: ethers.utils.hexlify(9800000), // Gas limit aumentato per sicurezza
      });

      console.log("⏳ Transaction sent, awaiting confirmation...");
      await tx.wait(); // Aspetta la conferma
     
      await checkBonusTimeRemaining(walletClient); // Aggiorna immediatamente lo stato


      alert("✅ Liquidity successfully provided!");
    } catch (error) {
      console.error("❌ Error during add liquidity:", error);
      alert("❌ Error: " + error.message);
    }
  }

  async function checkBonusTimeRemaining(walletClient) {
    try {
      if (!isConnected || !address) {
        console.log("⏳ Wallet not connected, cannot verify bonus.");
        return;
      }

      const provider = getProvider(walletClient) || new ethers.providers.Web3Provider(window.ethereum);
      const contract = new ethers.Contract(contractAddress, contractABI, provider);

      const depositTime = await contract.depositTimestamp(address);
      if (depositTime.toNumber() === 0) {
        console.log("❌ No deposits found. You can provide liquidity!");
       
        return;
      }

      // Calcola il tempo rimanente
      const currentTime = Math.floor(Date.now() / 1000); // Tempo attuale in secondi
      const unlockTime = depositTime.toNumber() + (30 * 24 * 60 * 60); // Aggiunge 30 giorni
      const remaining = unlockTime - currentTime;

      if (remaining <= 0) {
        setTimeRemaining("✅ Bonus available for withdrawal!");
      } else {
        const days = Math.floor(remaining / (24 * 60 * 60));
        const hours = Math.floor((remaining % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((remaining % (60 * 60)) / 60);
        setTimeRemaining(`⏳ You can provide liquidity again between: ${days}d ${hours}h ${minutes}m`);
      }
    } catch (error) {
      console.error("Error recovering time for bonus:", error);
      setTimeRemaining("Error calculating remaining time.");
    }
  }

  useEffect(() => {
    if (isConnected) {
      checkBonusTimeRemaining(walletClient);
      const interval = setInterval(() => {
        checkBonusTimeRemaining(walletClient);
        checkBonusAmount(walletClient);
       }, 60000); // Aggiorna ogni 60 secondi
      return () => clearInterval(interval);
    }
  }, [isConnected, address, walletClient]);

  async function checkBonusAmount(walletClient) {
    try {
      if (!isConnected || !address) {
        console.log("⚠️ Wallet not connected, cannot verify bonus.");
        return;
      }

      const provider = getProvider(walletClient);
      const contract = new ethers.Contract(contractAddress, contractABI, provider);

      // 📌 Ottiene il bonus disponibile per l'utente
      const bonusAmount = await contract.userBonuses(address);

      if (bonusAmount.toString() === "0") {
        console.log("❌ No bonuses available.");
        setBonusAmount("No bonuses available");
        return;
      }

     

      // Converti il valore da wei a APTESG (considerando 18 decimali)
      const formattedBonus = ethers.utils.formatUnits(bonusAmount, 18);
      setBonusAmount(`${formattedBonus} APTESG`);
      console.log("💰 bonus available:", formattedBonus, "APTESG");

    } catch (error) {
      console.error("Error retrieving bonus amount:", error);
      setBonusAmount("Bonus recovery error");
    }
   
  }

  // funzione fetchVestedTokens
   const fetchVestedTokens = async () => {
  try {
    if (!isConnected || !address) return;
    const provider = getProvider();
    if (!provider) {
      console.warn("Provider not available");
      setVestedTokens("0");
      return;
    }
    const contract = new ethers.Contract(contractAddress, contractABI, provider);

    // vesting[address] -> { totalAmount, claimedAmount, startTime, lastClaimTime }
    const v = await contract.vesting(address);

    const total = ethers.BigNumber.from(v.totalAmount);
    const claimed = ethers.BigNumber.from(v.claimedAmount);
    const last = Number(v.lastClaimTime);

    if (total.isZero()) {
      setVestedTokens("0"); // nessun vesting
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const daysElapsed = Math.floor((now - last) / (24 * 60 * 60));

    const remaining = total.sub(claimed);
    let toClaim = ethers.BigNumber.from(0);

    if (daysElapsed > 0) {
      const dailyRate = total.mul(2).div(100); // 2% del totale al giorno
      toClaim = dailyRate.mul(daysElapsed);
      if (toClaim.gt(remaining)) toClaim = remaining;
    }

    setVestedTokens(ethers.utils.formatUnits(toClaim, 18)); // string, es. "0.0000"
  } catch (err) {
    console.error("fetchVestedTokens error:", err);
    setVestedTokens("0");
  }
};

  async function claimLiquidityBonus() {
    try {
      const provider = getProvider(walletClient, { requireSigner: true });
      if (!provider) {
        alert("❌ Wallet not connected. Please reconnect.");
        return;
      }
      const contract = new ethers.Contract(contractAddress, contractABI, provider.getSigner());
      const tx = await contract.claimLiquidityBonus();
      await tx.wait();
      alert("Liquidity Bonus Successfully Redeemed!");
    } catch (error) {
      console.error("Bonus Redemption Error:", error);
      alert("Error: " + error.message);
    }
  }


  // Calcola automaticamente la quantità che riceverà l'utente
  const handleLiquidityInput = (e) => {
    const ethInput = e.target.value;
    setEthAmount(ethInput);

    if (ethInput && !isNaN(ethInput) && parseFloat(ethInput) > 0 && tokenPrice) {
      const tokenAmount = parseFloat(ethInput) / tokenPrice; // Converti ETH in APTESG
      setEstimatedLiquidityBonus(`${tokenAmount.toFixed(4)} APTESG`);
    } else {
      setEstimatedLiquidityBonus("Please enter a valid amount");
    }
  };

  //funzione claimTokens
  const claimTokens = async () => {
  if (!isConnected || !address) {
    alert("⚠️ Connect your wallet first!");
    return;
  }

  if (!(await switchToArbitrum(walletClient, connector))) return;

  try {
    const provider = getProvider(walletClient, { requireSigner: true });
    if (!provider) {
      alert("❌ Wallet not connected. Please reconnect.");
      return;
    }
    const signer = provider.getSigner();
    const contract = new ethers.Contract(contractAddress, contractABI, signer);

    const tx = await contract.claimTokens();
    await tx.wait();

    alert("✅ Tokens claimed successfully!");
    fetchVestedTokens(); // aggiorna dopo il claim
  } catch (error) {
    console.error("❌ Error claiming tokens:", error);
    alert("❌ Claim failed: " + error.message);
  }
};


  //funzioni staking
  // ✅ Controlla il saldo APTESG disponibile per staking
  const checkAptesgBalance = async (walletClient) => {
    const provider = getProvider(walletClient);
    const contract = new ethers.Contract(contractAddress, contractABI, provider);

    const balance = await contract.balanceOf(address);
    return ethers.utils.formatUnits(balance, 18);
  };

  // ✅ Funzione per depositare token in staking
  const stakeTokens = async (walletClient) => {
    if (!isConnected || !address) {
      alert("⚠️ Connect your wallet first!");
      return;
    }

    if (!(await switchToArbitrum(walletClient, connector))) return;
    // ✅ Verifica se l'utente ha completato il KYC
    try {
      const provider = getProvider(walletClient);
      const contract = new ethers.Contract(contractAddress, contractABI, provider);
      const isVerified = await contract.verifiedInvestors(address);

      if (!isVerified) {
        alert("❌ You must complete KYC before staking!");
        return;
      }
    } catch (error) {
      console.error("⚠️ Error checking KYC verification:", error);
      alert("⚠️ Unable to verify KYC. Try again later.");
      return;
    }

    if (!stakingAmount || isNaN(stakingAmount) || Number(stakingAmount) <= 0) {
      alert("⚠️ Enter a valid amount to stake!");
      return;
    }

    const aptesgBalance = await checkAptesgBalance(walletClient);
    if (parseFloat(aptesgBalance) < parseFloat(stakingAmount)) {
      alert(`⚠️ Insufficient APTESG balance! Your balance: ${aptesgBalance}`);
      return;
    }

    try {
      const provider = getProvider(walletClient, { requireSigner: true });
      if (!provider) {
        alert("❌ Wallet not connected. Please reconnect.");
        return;
      }
      const signer = provider.getSigner();
      const contract = new ethers.Contract(contractAddress, contractABI, signer);
      const parsedAmount = ethers.utils.parseUnits(stakingAmount.toString(), 18);

      let approveTx = await contract.approve(contractAddress, parsedAmount);
      await approveTx.wait();

      const tx = await contract.stakeTokens(parsedAmount);
      await tx.wait();

      alert("✅ Tokens staked successfully!");
      setStakingAmount("");
      checkStakingStatus(walletClient); // Aggiorna lo stato dello staking
    } catch (error) {
      console.error("❌ Error staking tokens:", error);
      alert("❌ Staking failed: " + error.message);
    }
  };

  // funzione per permettere all'utente quanti APTESG riceverà
  const handleStakingInput = (e) => {
    const stakingInput = e.target.value;
    setStakingAmount(stakingInput);

    if (stakingInput && !isNaN(stakingInput) && parseFloat(stakingInput) > 0) {
      // Converti il valore in wei (considerando 18 decimali del token)
      const stakingAmountInTokens = ethers.utils.parseUnits(stakingInput, 18);

      // Aggiungi il 10% di bonus (moltiplica per 110 e dividi per 100)
      const bonusAmount = stakingAmountInTokens.mul(110).div(100);

      // Converti in formato leggibile e rimuovi zeri decimali inutili
      const formattedBonus = parseFloat(ethers.utils.formatUnits(bonusAmount, 18));

      setEstimatedStakingBonus(`${formattedBonus} APTESG`);
    } else {
      setEstimatedStakingBonus("Please enter a valid amount");
    }
  };

  // ✅ Funzione per monitorare il tempo rimanente nello staking
  const checkStakingStatus = async (walletClient) => {
    if (!isConnected || !address) return;

    try {
      const provider = getProvider();
      if (!provider) {
        console.warn("Provider not available");
        return;
      }
      const contract = new ethers.Contract(contractAddress, contractABI, provider);
      const stakingData = await contract.stakingData(address);

      if (stakingData.amount.toString() === "0") {
        setStakingDetails(null);
        setTimeRemainingStaking("No active staking");
        return;
      }

      const stakingStartTime = Number(stakingData.startTime);
      const currentTime = Math.floor(Date.now() / 1000);
      const elapsedTime = currentTime - stakingStartTime;
      const remainingTime = 365 * 24 * 60 * 60 - elapsedTime;

      let daysRemaining = Math.floor(remainingTime / (24 * 60 * 60));
      let hoursRemaining = Math.floor((remainingTime % (24 * 60 * 60)) / (60 * 60));

      if (remainingTime <= 0) {
        setTimeRemainingStaking("✅ Bonus fully matured!");
      } else {
        setTimeRemainingStaking(`⏳ Time left: ${daysRemaining}d ${hoursRemaining}h`);
      }

      setStakingDetails({
        stakedAmount: ethers.utils.formatUnits(stakingData.amount, 18),
        startTimeFormatted: new Date(stakingStartTime * 1000).toLocaleString(),
      });

    } catch (error) {
      console.error("❌ Error checking staking status:", error);
      setTimeRemainingStaking("Error retrieving staking data");
      setStakingDetails(null);
    }
  };

  // ✅ Funzione per ritirare lo staking con avviso penalità
  const withdrawStake = async (walletClient) => {
    if (!isConnected || !address) {
      alert("⚠️ Connect your wallet first!");
      return;
    }

    if (!(await switchToArbitrum(walletClient, connector))) return;

    try {
      const provider = getProvider(walletClient, { requireSigner: true });
      if (!provider) {
        alert("❌ Wallet not connected. Please reconnect.");
        return;
      }
      const contract = new ethers.Contract(contractAddress, contractABI, provider.getSigner());
      const stakingData = await contract.stakingData(address);
      const stakingStartTime = Number(stakingData.startTime);
      const currentTime = Math.floor(Date.now() / 1000);
      const elapsedTime = currentTime - stakingStartTime;

      let penaltyPercentage = 0;
      let remainingTime = 365 * 24 * 60 * 60 - elapsedTime;

      if (elapsedTime < 90 * 24 * 60 * 60) {
        penaltyPercentage = 100;
      } else if (elapsedTime < 180 * 24 * 60 * 60) {
        penaltyPercentage = 70;
      } else if (elapsedTime < 270 * 24 * 60 * 60) {
        penaltyPercentage = 50;
      } else if (elapsedTime < 365 * 24 * 60 * 60) {
        penaltyPercentage = 25;
      }

      if (penaltyPercentage > 0) {
        const daysRemaining = Math.floor(remainingTime / (24 * 60 * 60));
        const confirmation = window.confirm(
          `⚠️ If you withdraw now, you will lose ${penaltyPercentage}% of your bonus.\n` +
          `⏳ Time remaining to complete staking: ${daysRemaining} days.\n` +
          `Do you want to continue?`
        );

        if (!confirmation) return;
      }

      const tx = await contract.withdrawStake(ethers.utils.parseUnits(stakingData.amount, 18));
      await tx.wait();

      alert("✅ Stake withdrawn successfully!");
      checkStakingStatus(walletClient); // Aggiorna lo stato dello staking
    } catch (error) {
      console.error("❌ Error withdrawing stake:", error);
      alert("❌ Withdrawal failed: " + error.message);
    }
  };

  // ✅ Funzione per riscattare il bonus di staking
  const claimStakingBonus = async (walletClient) => {
    if (!isConnected || !address) {
      alert("⚠️ Connect your wallet first!");
      return;
    }

    if (!(await switchToArbitrum(walletClient, connector))) return;

    try {
      const provider = getProvider(walletClient, { requireSigner: true });
      if (!provider) {
        alert("❌ Wallet not connected. Please reconnect.");
        return;
      }
      const signer = provider.getSigner();
      const contract = new ethers.Contract(contractAddress, contractABI, signer);

      const tx = await contract.claimStakingBonus();
      await tx.wait();

      setClaimedBonus("Bonus claimed successfully!");

      alert("✅ Bonus claimed successfully!");
      checkStakingStatus(walletClient);
    } catch (error) {
      console.error("❌ Error claiming staking bonus:", error);
      alert("❌ Bonus claim failed: " + error.message);
    }
  };

  // ✅ Aggiornamento automatico dello stato dello staking ogni 60 secondi
  useEffect(() => {
    if (isConnected) {
      checkStakingStatus(walletClient);
      const interval = setInterval(() => {
        checkStakingStatus(walletClient);
     }, 60000);
      return () => clearInterval(interval);
    }
  }, [isConnected, address]);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isMobile = /android|iphone|ipad|ipod/i.test(ua);
    const hasEthereum = typeof window.ethereum !== 'undefined';
    //const isWalletBrowser = /metamask|trust|coinbase|rainbow|brave|argent|zerion|phantom/i.test(ua);
 
    if (isMobile && /*!isWalletBrowser*/ !hasEthereum) {
      setShowBrowserWarning(true);
    } else {
      setShowBrowserWarning(false);
    }
  }, []);

  // Controlla esclusivamente la chain del connettore wallet attivo.
  useEffect(() => {
    if (!isConnected) {
      setWrongNetwork(false);
      return undefined;
    }

    const timeout = setTimeout(async () => {
      let activeChainId = chain?.id ?? walletClient?.chain?.id;
      if (!activeChainId && walletClient?.getChainId) {
        try {
          activeChainId = await walletClient.getChainId();
        } catch (error) {
          console.warn("Unable to read chain from walletClient", error);
        }
      }

      if (!activeChainId && connector?.getProvider) {
        try {
          const activeProvider = await connector.getProvider();
          const chainIdHex = await activeProvider.request({ method: "eth_chainId" });
          activeChainId = Number.parseInt(chainIdHex, 16);
        } catch (error) {
          console.warn("Unable to read chain from the active connector", error);
        }
      }

      setWrongNetwork(Boolean(activeChainId && activeChainId !== ARBITRUM_CHAIN_ID));
    }, 250);

    return () => clearTimeout(timeout);
  }, [isConnected, chain, connector, walletClient]);

  useEffect(() => {
    if (isConnected && walletClient && !refreshTriggeredRef.current) {
      console.log("✅ Wallet connected, refresh refreshKey");
      setRefreshKey(prev => prev + 1);
      refreshTriggeredRef.current = true;
      console.log("Refresh key incremented only one time. ");
    }
  }, [isConnected, walletClient]);

  const currentChainName = chain?.name ?? walletClient?.chain?.name;
  const currentChainId = chain?.id ?? walletClient?.chain?.id;

 return (
    <div className="App" key={refreshKey}>
          <header className="App-header">
            {/* Hero Section - Logo e Titolo */}
            <div className="hero-section">
              <div className="logo-container">
                <img src={logo} alt="Logo" className="logo" />
                <h1>AppleTree (APTESG)</h1>
                <h2 className="subtitle">The currency of a sustainable future!</h2>
              </div>

              {/* Wallet Connection */}
              <section className="wallet-section">
                {/* {showBrowserWarning && (
                  <div style={{
                    backgroundColor: "#fff3cd",
                    color: "#856404",
                    padding: "12px",
                    border: "1px solid #ffeeba",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    textAlign: "center",
                    fontWeight: "bold",
                    width: "100%",
                    maxWidth: "500px"
                  }}>
                    Warning: Some features may not work correctly on Browser Mobile.<br />
                    For the best experience, please use a Web3-compatible browser like <strong>MetaMask, Trust Wallet</strong>, or switch to a desktop browser.
                  </div>
                )} */}

                {wrongNetwork && (
                  <div style={{
                    backgroundColor: "#fff3cd",
                    color: "#856404",
                    padding: "12px",
                    border: "1px solid #ffeeba",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    textAlign: "center",
                    fontWeight: "bold",
                    width: "100%",
                    maxWidth: "500px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    alignItems: "center"
                  }}>
                    <div>
                      ⚠️ You are connected to the wrong network.
                      {currentChainId && (
                        <span style={{ display: "block", fontSize: "14px", marginTop: "5px" }}>
                          Current: {currentChainName || 'Unknown'} (Chain ID: {currentChainId})
                        </span>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        await switchToArbitrum(walletClient, connector);
                      }}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#18A34A",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        transition: "background-color 0.2s"
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#15893d"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "#18A34A"}
                    >
                      🔄 Switch to Arbitrum Mainnet
                    </button>
                  </div>
                )}

                {!isConnected ? (
                  <button className="wallet-connect-btn" onClick={connectWallet}>
                    🔐 Connect Wallet
                  </button>
                ) : (
                  <div className="wallet-connected-widget">
                    <div className="wallet-avatar">
                      {ensAvatar ? (
                        <img src={ensAvatar} alt="ENS Avatar" style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
                      ) : (
                        "💼"
                      )}
                    </div>
                    <div className="wallet-info">
                      <div className="wallet-label">Connected Wallet</div>
                      <div className="wallet-address">
                        {ensName || `${address?.slice(0, 6)}...${address?.slice(-4)}`}
                      </div>
                    </div>
                    <button className="wallet-disconnect-btn" onClick={() => {
                      disconnect();
                      console.log("👋 Wallet disconnesso");
                    }}>
                      Disconnect
                    </button>
                  </div>
                )}
              </section>
            </div>

            {/* ESG Dashboard - Sezione Prominente */}
            {SHOW_LEGACY_HOME_CARDS && (
              <div className="esg-hero-card">
                <h2 className="esg-hero-title">🌱 ESG Certification Platform</h2>
                <p className="esg-hero-description">
                  Certify your company's sustainability practices and unlock exclusive benefits.
                  Access our comprehensive ESG dashboard to manage your environmental, social, and governance metrics.
                </p>
                <Link to="/esg-dashboard">
                  <button className="esg-dashboard-btn">
                    Open ESG Dashboard →
                  </button>
                </Link>
              </div>
            )}

            {/* Main Content Grid */}
            <div className="main-grid">

            {/* Buy section */}
            <BuyAPTESG
  address={address}
  isConnected={isConnected}
/>

            {/* Token Chart - DexTools Widget */}
            <section className="token-chart-large">
              <h2 style={{ marginBottom: "20px", fontSize: "1.5rem", textAlign: "center" }}>APTESG Token Chart</h2>
              <DexToolsWidget />
            </section>
           
            {/* KYC section */}
            {SHOW_LEGACY_HOME_CARDS && (
              <section className="kyc-section" style={{
              gridColumn: "1 / -1",
              justifySelf: "stretch",
              margin: "20px 0"
            }}>
              <h2 style={{ fontSize: "1.3rem", marginBottom: "8px" }}>Complete Your KYC Verification</h2>
              <p style={{ color: "gray", marginBottom: "6px", fontSize: "0.9rem" }}>
            *To access all features and help keep our community safe, complete your KYC verification in just a few easy steps*
            </p>
{/* <p
  style={{
    textAlign: 'center',
    fontSize: '15px',
    color: 'gray',
    marginTop: "8px",
    marginBottom: "10px"
  }}
>
  *Learn more {' '}
  <a
    href="https://appletreetoken.com/about-kyc"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      color: '#4FC3F7',
      textDecoration: 'underline',
      cursor: 'pointer',
    }}
    onMouseEnter={(e) => (e.target.style.color = '#81D4FA')}
    onMouseLeave={(e) => (e.target.style.color = '#4FC3F7')}
  >
    about KYC
  </a>*
</p> */}
              {kycFee > 0 ? (
                <>
                  <p style={{ marginBottom: "8px", fontSize: "0.95rem" }}>KYC Fee: <span style={{color: "yellow" }}>{kycFee} ETH</span></p>
                  <button className="btn btn-main" style={{ padding: "10px 20px", fontSize: "0.95rem" }} onClick={async () => {
                    await payForKYC(address); // Effettua il pagamento
                    setTimeout(checkKYCStatus, 5000); // Dopo 5 secondi verifica lo stato
                  }} disabled={kycStatus}>
                    {kycStatus ? "KYC Verified ✅" : "Pay KYC"}
                  </button>
                </>
              ) : (
                <p style={{ fontSize: "0.9rem" }}>Retrieving KYC fee...</p>
              )}
           
              <h2 style={{ fontSize: "1.1rem", marginTop: "12px", marginBottom: "6px" }}>Status:</h2>
              <p style={{ color: kycStatus ? "yellow" : "red", fontSize: "0.95rem", marginBottom: "8px" }}>
                {kycStatus ? "KYC Completed ✅" : "KYC Pending ❌"}
              </p>

            {hasPaidButNotVerified && (
              <div className="kyc-resume">
                <p style={{ color: "#ffc107" }}>You've paid for KYC but haven't completed it.</p>
                <button
                className="btn btn-orange"
                onClick={() => resumeKYC(walletClient, address)}
                >
                  Resume KYC Verification
                  </button>
                  </div>
                )}
              </section>
            )}

            </div>

{/* Bonus Info Separator */}
            {/* <div className="bonus-info-separator" style={{ marginTop: "10px", marginBottom: "3px" }}>
              <p>
                *Discover how the bonuses work by visiting our guide{' '}
                <a
                  href="https://appletreetoken.com/bonus-system"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#4FC3F7',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => (e.target.style.color = '#81D4FA')}
                  onMouseLeave={(e) => (e.target.style.color = '#4FC3F7')}
                >
                  here
                </a>*
              </p>
            </div> */}

            {/* Liquidity & Staking Grid */}
            {SHOW_LEGACY_HOME_CARDS && (
              <div className="main-grid">

            {/* Sezione Provide Liquidity */}
            <section className="liquidity-section">
                <h2>Provide Liquidity</h2>
                {/* Widget stato liquidità */}
                <LiquidityStatusWidget address={address} />
                <div>
                <p style={{ color: "gray", marginBottom: "5px" }}>
                  *Just enter the amount of ETH, the contract will automatically calculate the amount of APTESG!*
                </p>
                 <p style={{ color: "orange", marginBottom: "5px" }}>
                  *You can add liquidity only once every 30 days - the same time period required to withdraw your bonus*
                </p>
                    {/* Input per ETH (l'utente inserisce il valore) */}
                    <label>Amount of ETH:</label>
                    <div style={{ display: "flex", justifyContent: "center"}}>
                    <input
                      type="number"
                      placeholder="Enter ETH amount"
                      value={ethAmount}
                      onChange={handleLiquidityInput} // ✅ Calcola il valore stimato
                    />
                    </div>
               

                 
                    {/* Testo per il valore stimato in APTESG */}
                    <label>Estimated APTESG:</label>
                    <span style={{ color: estimatedLiquidityBonus.includes("Inserisci") ? "white" : "yellow", fontWeight: "bold" }}>
                      {estimatedLiquidityBonus}
                    </span>
                 
                  <div>
                  <button
  className="btn btn-main"
  onClick={() => checkAndProvideLiquidity(walletClient, ethAmount)}
  disabled={timeRemaining?.includes("⏳")} // Disabilita solo se in attesa
>
  Provide Liquidity
</button>
                  {timeRemaining && (
  <p style={{
    color: timeRemaining.includes("✅") ? "green" :
           timeRemaining.includes("⏳") ? "orange" : "white",
    marginTop: "10px"
  }}>
    {timeRemaining}
  </p>
)}
</div>


<div style={{ marginTop: "20px" }}>
  <p>
    <strong>Vested Tokens Available:</strong>{" "}
    <span style={{ color: "yellow" }}>
      {Number(vestedTokens).toFixed(4)} APTESG
    </span>
  </p>

  <p style={{ color: "orange", marginBottom: 8 }}>
    *You can claim up to 2% of your vested tokens daily.*
  </p>

  <button
    className="btn btn-green"
    onClick={claimTokens}
    disabled={Number(vestedTokens) <= 0}
    title={Number(vestedTokens) <= 0 ? "Nothing claimable yet. Try again after 24h." : ""}
  >
    Claim Tokens
  </button>
</div>


                 
                  <button className="btn btn-blue" onClick={checkBonusAmount}>
                    Check Bonus Amount
                  </button>
                  <p><strong>Bonus available:</strong><span style={{ color: "yellow" }}> {bonusAmount}</span></p>
               

               
                  <button className="btn btn-blue" onClick={checkBonusTimeRemaining}>
                    Check Bonus Time
                  </button>
                  <p>{timeRemaining || "Caricamento..."}</p>
                 


                <button className="btn btn-green" onClick={claimLiquidityBonus} disabled={timeRemaining && timeRemaining.includes("⏳")}>
                  Claim Liquidity Bonus
                </button>
              </div>
            </section>

            {/* Staking Details */}
            <section className="staking-section">
              <h2>Staking</h2>
              <div>
                  {/* Input per lo staking (l'utente inserisce il valore) */}
                  <label>Amount to Stake:</label>
                  <input
                    type="number"
                    placeholder="Enter APTESG amount"
                    value={stakingAmount}
                    onChange={handleStakingInput} // ✅ Calcola il valore stimato
                  />
                </div>

                {/* Testo per il valore stimato in APTESG con bonus */}
               
                  <label>Estimated Bonus (+10%):</label>
                  <span style={{ color: estimatedStakingBonus.includes("inserisci") ? "white" : "yellow", fontWeight: "bold" }}>
                    {estimatedStakingBonus}
                  </span>
               

                <button className="btn btn-blue" onClick={stakeTokens}>
                  Stake Tokens
                </button>
                <button className="btn btn-orange" onClick={withdrawStake}>
                  Withdraw Stake
                </button>
                <button className="btn btn-main" onClick={claimStakingBonus}>
                  Claim Bonus
                </button>
             


              {/* Mostra tempo rimanente e dettagli staking */}
              <p>{timeRemainingStaking}</p>
              {stakingDetails && (
                <p><strong>Staked Amount:</strong> <span style={{ color: "yellow" }}>{stakingDetails.stakedAmount} Tokens</span></p>
              )}
              {/* Mostra il messaggio del bonus riscattato */}
              <p><strong>Claimed Bonus:</strong>
              <span style={{ color: "yellow" }}>{claimedBonus || "No bonus claimed yet"}</span>
              </p>
            </section>

              </div>
            )}

            {/* Treasury & Donations - Footer Section */}
            <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "40px" }}>
              <section className="treasury">          
                <h2 style={{ fontSize: "1.3rem", marginBottom: "8px" }}>Treasury Balance</h2>
                <p style={{ marginBottom: "5px", fontSize: "0.95rem" }}>
                  <strong>ETH Balance: <span style={{ color: "yellow" }}>
                    {treasuryBalance ? `${parseFloat(treasuryBalance).toFixed(6)} ETH` : "Loading..."}
                    {treasuryBalance && ethPriceUSD && (
                      <span style={{ color: "#aaa", fontSize: "0.85rem", marginLeft: "8px" }}>
                        (~${(parseFloat(treasuryBalance) * ethPriceUSD).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USD)
                      </span>
                    )}
                  </span></strong>
                </p>
                <p style={{ marginBottom: "10px", fontSize: "0.95rem" }}>
                  <strong>APTESG Balance: <span style={{ color: "#4CAF50" }}>
                    {treasuryTokenBalance ? `${parseFloat(treasuryTokenBalance).toLocaleString('en-US', {maximumFractionDigits: 2})} APTESG` : "Loading..."}
                    {treasuryTokenBalance && aptESGPriceUSD && (
                      <span style={{ color: "#aaa", fontSize: "0.85rem", marginLeft: "8px" }}>
                        (~${(parseFloat(treasuryTokenBalance) * aptESGPriceUSD).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USD)
                      </span>
                    )}
                  </span></strong>
                </p>
                
                <div style={{ marginTop: "15px" }}>
                  <p style={{ color: "gray", marginBottom: "10px", fontSize: "0.9rem" }}>
                    *Donate any amount of ETH to support the growth of AppleTree*
                  </p>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: "10px" }}>
                    <input
                      type="number"
                      placeholder="Enter amount in ETH"
                      value={donationAmount}
                      onChange={(e) => setDonationAmount(e.target.value)}
                      style={{ marginRight: "10px", padding: "8px", width: "200px", fontSize: "0.9rem" }}
                    />
                  </div>
                  <button className="btn btn-main" style={{ padding: "10px 20px", fontSize: "0.95rem" }} onClick={() =>
                    donateToTreasury(walletClient, donationAmount)}
                  >
                    Donate to Treasury
                  </button>
                </div>
              </section>
            </div>

            {/* Footer Contact */}
            <div style={{ color: "gray", marginTop: "40px", marginBottom: "20px", fontSize: "0.8rem", textAlign: "center" }}>
              Having issues or found a bug? Contact us at
              <br />
              <span style={{ color: "#20f804", fontWeight: "bold" }}>
                info@appletreetoken.com
              </span>
            </div>

        </header>
        <LegalFooter />
        </div>
  );
}

/**
 * Widget pubblico che mostra lo stato del programma di liquidità.
 * Inserito nella sezione "Provide Liquidity" della homepage.
 */
function LiquidityStatusWidget({ address }) {
  const { status, loading, error } = useLiquidityStatus();
  const { info: walletInfo } = useWalletLiquidity(address || null);

  if (loading) {
    return (
      <div style={lw.wrapper}>
        <div style={lw.loading}>Loading liquidity status...</div>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div style={lw.wrapper}>
        <div style={{ ...lw.loading, color: "#a0aec0", fontSize: 12 }}>
          ⚠️ Liquidity status unavailable.
        </div>
      </div>
    );
  }

  const pct = parseFloat(status.weeklyUsedPercent) || 0;
  const paused = status.isPaused;

  const barColor =
    paused || pct >= 100
      ? "#e53e3e"
      : pct >= 75
      ? "#dd6b20"
      : "#38a169";

  return (
    <div style={lw.wrapper}>
      {/* Banner pausa */}
      {paused && (
        <div style={lw.pauseBanner}>
          🔴 <strong>Il programma di liquidità è temporaneamente sospeso.</strong>
          {status.pausedReason && (
            <span style={{ marginLeft: 8, fontWeight: 400 }}>
              Reason: {status.pausedReason}
            </span>
          )}
        </div>
      )}

      {/* Banner warning */}
      {!paused && pct >= 75 && pct < 100 && (
        <div style={lw.warningBanner}>
          ⚠️ Weekly limit almost reached ({pct}%). New liquidity provisions may be blocked soon.
        </div>
      )}

      {/* Barra progresso settimanale */}
      <div style={lw.row}>
        <span style={lw.label}>Weekly capacity</span>
        <span style={lw.pct}>{pct}%</span>
      </div>
      <div style={lw.track}>
        <div
          style={{
            ...lw.bar,
            width: `${Math.min(pct, 100)}%`,
            backgroundColor: barColor,
          }}
        />
      </div>
      <div style={lw.statsRow}>
        <span>Used: <strong style={{ color: "#fff" }}>{status.weeklyUsed} ETH</strong></span>
        <span>Remaining: <strong style={{ color: "#fff" }}>{status.weeklyRemaining} ETH</strong></span>
        <span>Max: <strong style={{ color: "#fff" }}>{status.weeklyMax} ETH</strong></span>
      </div>

      {/* Info wallet connesso / limite per operazione */}
      <div style={lw.walletInfo}>
        {walletInfo ? (
          walletInfo.isFlagged ? (
            <span style={lw.flagged}>
              ⛔ Wallet not authorized to provide liquidity.{" "}
              {walletInfo.flagInfo?.reason && <em>{walletInfo.flagInfo.reason}</em>}
            </span>
          ) : (
            <span>
              💧 You can still provide up to{" "}
              <strong style={{ color: "#4ade80" }}>{walletInfo.walletRemaining} ETH</strong>{" "}
              in this operation.
              <br />
              <span style={{ fontSize: 11, color: "#fc8181", marginTop: 4, display: "inline-block" }}>
                ⚠️ Exceeding this limit will flag your wallet and revoke your provider status.
              </span>
            </span>
          )
        ) : (
          <span>
            💧 Max per operation:{" "}
            <strong style={{ color: "#4ade80" }}>{status.walletLimit} ETH</strong>
            <br />
            <span style={{ fontSize: 11, color: "#fc8181", marginTop: 4, display: "inline-block" }}>
              ⚠️ Exceeding this limit will flag your wallet and revoke your provider status.
            </span>
          </span>
        )}
      </div>

      {/* Reset finestra */}
      {status.windowResetAt && (
        <div style={lw.resetNote}>
          Next window reset:{" "}
          <strong>{new Date(status.windowResetAt).toLocaleDateString("en-US")}</strong>
        </div>
      )}
    </div>
  );
}

const lw = {
  wrapper: {
    backgroundColor: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8,
    padding: "14px 16px",
    marginBottom: 16,
    color: "#e2e8f0",
    fontSize: 14,
  },
  loading: { color: "#a0aec0", textAlign: "center", padding: 8 },
  pauseBanner: {
    backgroundColor: "#c53030",
    color: "white",
    borderRadius: 6,
    padding: "8px 12px",
    marginBottom: 10,
    fontSize: 13,
  },
  warningBanner: {
    backgroundColor: "#c05621",
    color: "white",
    borderRadius: 6,
    padding: "8px 12px",
    marginBottom: 10,
    fontSize: 13,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  label: { fontSize: 13, color: "#a0aec0" },
  pct: { fontSize: 13, fontWeight: 700 },
  track: {
    backgroundColor: "rgba(255,255,255,0.12)",
    borderRadius: 4,
    height: 10,
    marginBottom: 8,
    overflow: "hidden",
  },
  bar: {
    height: "100%",
    borderRadius: 4,
    transition: "width 0.5s ease",
  },
  statsRow: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    fontSize: 12,
    color: "#a0aec0",
    marginBottom: 8,
  },
  walletInfo: {
    fontSize: 13,
    padding: "6px 0",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    marginTop: 4,
  },
  flagged: { color: "#fc8181" },
  resetNote: {
    fontSize: 11,
    color: "#718096",
    marginTop: 4,
  },
};

export default App;
